import type { SmartPasteBlockCode, SmartPasteWarning } from '@/types/smartPaste';

/**
 * Turns core's Smart Paste warning / block codes into merchant-facing text.
 *
 * Kept out of the card so the wording is unit-testable and so both languages go
 * through the same path — the parser's own `warnings[]` are English-only, which
 * is exactly why core wraps everything it can in a code we own.
 */

type Translate = (key: string, options?: Record<string, unknown>) => unknown;

/** One line per warning, or null for a warning we have nothing useful to say about. */
export function warningText(warning: SmartPasteWarning, t: Translate): string | null {
  if (warning.code === 'PHONE_INVALID') {
    const key = warning.field === 'phone2' ? 'smart_paste_warn_phone2' : 'smart_paste_warn_phone';
    return String(t(key, { value: warning.value }));
  }

  if (warning.code === 'MULTIPLE_CITIES') {
    return String(t('smart_paste_warn_cities', { used: warning.used ?? '', other: warning.value }));
  }

  // The form filled in, minus the destination. Without this line the merchant's
  // only clue is an empty City dropdown on an otherwise complete form.
  if (warning.code === 'MISSING_CITY') {
    return String(t('smart_paste_warn_missing_city'));
  }

  // `PARSER` — upstream diagnostics like "AI response could not be parsed."
  // English-only, about the model rather than the order, and there is nothing a
  // merchant can do with it. Core still returns it (support reads it off the
  // response); the screen says something they can act on instead.
  return null;
}

export function warningLines(warnings: SmartPasteWarning[] | undefined, t: Translate): string[] {
  return (warnings ?? [])
    .map((warning) => warningText(warning, t))
    .filter((line): line is string => !!line);
}

/**
 * Why we couldn't fill the form in. The code carries the useful part — the raw
 * `block_reasons` are upstream English and said "Missing city" even for a
 * message that had no order in it at all.
 */
export function blockedText(
  code: SmartPasteBlockCode | null | undefined,
  reasons: string[],
  t: Translate,
): string {
  if (code === 'UNREADABLE') return String(t('smart_paste_blocked_unreadable'));
  if (code === 'MISSING_CITY') return String(t('smart_paste_blocked_city'));

  const detail = reasons.filter((reason) => reason.trim() !== '').join(' ');
  return detail
    ? `${String(t('smart_paste_blocked_title'))} ${detail}`
    : String(t('smart_paste_blocked_title'));
}
